import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import type { User } from "../types";
import { sseService } from "../services/sseService";
import { isGuestAuthEnabled } from "../utils/guestAuth";

// GuestAuthContext value type
interface GuestAuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  loginGuest: () => void;
  logout: () => void;
  setError: (error: string | null) => void;
}

const GuestAuthContext = createContext<GuestAuthContextValue | undefined>(undefined);

interface GuestAuthProviderProps {
  children: ReactNode;
}

export const GuestAuthProvider: React.FC<GuestAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isGuestAuthEnabled()) {
      localStorage.removeItem("guestUser");
      setLoading(false);
      return;
    }

    const storedUser = localStorage.getItem("guestUser");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser) as User;
        setUser(parsed);
      } catch (error) {
        console.error("GuestAuthProvider: ì ì\\ë ì¬ì©ì ë°ì´í° íì± ì¤ë\\", error);
        localStorage.removeItem("guestUser");
      }
    } else {
      const guestUser: User = {
        uid: "guest123",
        email: "guest@example.com",
        displayName: "Guest user",
        token: "guest-jwt-token",
        isGuest: true,
      };

      setUser(guestUser);
      localStorage.setItem("guestUser", JSON.stringify(guestUser));
    }
    setLoading(false);
  }, []);

  const loginGuest = useCallback((): void => {
    if (!isGuestAuthEnabled()) {
      setError("Guest sign-in is not available.");
      return;
    }

    setLoading(true);
    setTimeout(() => {
      const guestUser: User = {
        uid: "guest123",
        email: "guest@example.com",
        displayName: "Guest user",
        token: "guest-jwt-token",
        isGuest: true,
      };

      setUser(guestUser);
      localStorage.setItem("guestUser", JSON.stringify(guestUser));
      setLoading(false);
    }, 1000);
  }, []);

  const logout = useCallback((): void => {
    sseService.disconnectAll();
    setUser(null);
    localStorage.removeItem("guestUser");
  }, []);

  const value = useMemo<GuestAuthContextValue>(
    () => ({
      user,
      loading,
      error,
      isAuthenticated: !!user,
      loginGuest,
      logout,
      setError,
    }),
    [user, loading, error, loginGuest, logout]
  );

  return (
    <GuestAuthContext.Provider value={value}>
      {children}
    </GuestAuthContext.Provider>
  );
};

export const useGuestAuth = (): GuestAuthContextValue => {
  const context = useContext(GuestAuthContext);
  if (!context) {
    throw new Error("useGuestAuth must be used within a GuestAuthProvider");
  }
  return context;
};
